import React, { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import MDialog from "components/Shared/MDialog.tsx";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import IncidentAttachments from "./IncidentAttachments.tsx";
import IncidentAdditionalNotes from "./IncidentAdditionalNotes";
import { CustomerServiceIncidentAttachmentMetadata, InternetUser } from "interfaces/Mueller.Business.Models.ts";
import { ClientSideIncident } from "./ClientSideIncident.ts";
import MSpinner from "components/Shared/MSpinner.tsx";
import { getUser } from "requests/internetUsers/index.js";
import { errorOptions } from "typeConstants/customerService/index.js";

interface Props {
  incident: ClientSideIncident;
  incidentDialogVisible: boolean;
  hideIncidentDialog: () => void;
  controlNumber: number;
}

const ViewIncidentDialog = (props: Props) => {

  const mounted = useRef<boolean>(true);

  const [attachments, setAttachments] = useState<CustomerServiceIncidentAttachmentMetadata[]>([]);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(true);
  const [internetuser, setInternetUser] = useState<InternetUser>(null);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setAttachments(props.incident.attachments?.slice(0));
    getUser(props.incident.assignedInternetUserId, getUserCallback, mounted);
  }, [props.incident]);

  const getUserCallback = (user) => {
    setInternetUser(user);
    setIsFetchingUser(false);
  };

  const { getRootProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
  });

  const getDialogActions = () => {
    return [{ secondary: false, children: "Cancel", onClick: props.hideIncidentDialog }];
  };

  if(isFetchingUser || !internetuser)
    return <MDialog><MSpinner /></MDialog>; //Since we're viewing a dialog with the CS note info, just seems 

  const mGridFormDefinition: any[] = [
    [
      { label: `Status:  ${props.incident.status}`}
    ],
    [
      { label: `Department:  ${props.incident.assignedDepartment}` }
    ],
    [
      { label: `Assigned To:  ${internetuser.firstName + " " + internetuser.lastName}` }
    ],
    [
      { label: `Person Interviewed:  ${props.incident.personTypeCode}`, source: "personTypeCode" }
    ],
    [
      { label: `Reply To Name:  ${props.incident.replyToName}`, source: "replyToName" }
    ],
    [
      { label: `Reply To Email:  ${props.incident.replyToEmail}`, source: "replyToEmail" }
    ],
    [
      { label: `Reply To Phone:  ${props.incident.replyToPhone}`, source: "replyToPhone" }
    ],
    [
      { label: `Error Found:  ${errorOptions.find(e => e.value === props.incident.errorOption).label}` }
    ],
    [
      { label: `Reason:  ${props.incident.reasonTypeCode}`, source: "reasonTypeCode" }
    ],
    [
      { dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Initial Note", source: "note" }
    ],
  ];

  if (props.incident.id) { // if is editing
    mGridFormDefinition.unshift([{ label: `Incident # ${props.incident.id}` }]);
  }
  
  mGridFormDefinition.unshift([
    { label: `Control # ${props.controlNumber}` }
  ]);

  const dropZoneBaseStyle = {
    border: "2px dashed transparent",
  };

  const activeStyle = {...dropZoneBaseStyle};

  return (
    <MDialog
      id="cs-add-edit-incident"
      className="cs-add-edit-incident-dialog"
      title={"View CS Incident"}
      visible={props.incidentDialogVisible}
      contentActions={getDialogActions()}
      focusOnMount={false}
      width={625}
    >
      <div {...getRootProps({style: activeStyle})}>
          <MGridForm
            width={600}
            data={props.incident}
            formDefinition={mGridFormDefinition}
            portalTarget={() => document.querySelector(".cs-add-edit-incident-dialog")}
          />
          <IncidentAttachments 
            incidentId={props.incident.id}
            attachments={attachments}
            disabled={true}
            viewOnly={true}
          />
        {props.incident.id && <IncidentAdditionalNotes 
          incident={props.incident}
          viewOnly={true}
        />}        
      </div>
    </MDialog>
  ); 
};

export default ViewIncidentDialog;